import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  ChevronRight,
  Filter,
} from 'lucide-react';

interface MemberAssessment {
  id: string;
  status: 'assigned' | 'in_progress' | 'completed' | 'expired';
  assigned_at: string;
  started_at: string | null;
  completed_at: string | null;
  due_date: string | null;
  template: {
    id: string;
    name: string;
    description: string;
    category: string;
  };
}

export default function MemberAssessments() {
  const [assessments, setAssessments] = useState<MemberAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const { data: membersData } = await supabase
        .from('members')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (!membersData) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('member_assessments')
        .select(
          `
          *,
          template:assessment_templates(id, name, description, category)
        `
        )
        .eq('member_id', membersData.id)
        .order('assigned_at', { ascending: false });

      if (error) throw error;
      setAssessments(data || []);
    } catch (error) {
      console.error('Error fetching assessments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assigned':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'in_progress':
        return <Clock className="w-4 h-4" />;
      case 'assigned':
        return <FileText className="w-4 h-4" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getDaysRemaining = (dueDate: string | null) => {
    if (!dueDate) return null;
    const days = Math.ceil(
      (new Date(dueDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
    );
    return days;
  };

  const filteredAssessments = assessments.filter((assessment) => {
    if (statusFilter === 'all') return true;
    return assessment.status === statusFilter;
  });

  const stats = {
    total: assessments.length,
    assigned: assessments.filter((a) => a.status === 'assigned').length,
    inProgress: assessments.filter((a) => a.status === 'in_progress').length,
    completed: assessments.filter((a) => a.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Assessments</h1>
        <p className="text-gray-600">
          Complete your assigned health assessments to access benefits
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{stats.assigned}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-amber-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            </div>
            <Clock className="w-8 h-8 text-blue-400" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
        </div>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Assessments</option>
          <option value="assigned">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="grid gap-4">
        {filteredAssessments.map((assessment) => {
          const daysRemaining = getDaysRemaining(assessment.due_date);
          const isUrgent = daysRemaining !== null && daysRemaining <= 3;

          return (
            <div
              key={assessment.id}
              className={`bg-white rounded-lg border ${
                isUrgent && assessment.status !== 'completed'
                  ? 'border-red-300 shadow-md'
                  : 'border-gray-200'
              } p-6 hover:shadow-lg transition-shadow`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <FileText className="w-6 h-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {assessment.template.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {assessment.template.description}
                      </p>

                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full border flex items-center gap-1 ${getStatusBadge(
                            assessment.status
                          )}`}
                        >
                          {getStatusIcon(assessment.status)}
                          {assessment.status.replace('_', ' ').toUpperCase()}
                        </span>

                        {assessment.template.category && (
                          <span className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                            {assessment.template.category}
                          </span>
                        )}

                        {assessment.due_date && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-1" />
                            Due:{' '}
                            {new Date(assessment.due_date).toLocaleDateString()}
                            {daysRemaining !== null && (
                              <span
                                className={`ml-2 ${
                                  isUrgent ? 'text-red-600 font-semibold' : ''
                                }`}
                              >
                                ({daysRemaining} days)
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-gray-500">
                        Assigned on{' '}
                        {new Date(assessment.assigned_at).toLocaleDateString()}
                        {assessment.completed_at && (
                          <>
                            {' '}
                            • Completed on{' '}
                            {new Date(assessment.completed_at).toLocaleDateString()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ml-4">
                  {assessment.status === 'completed' ? (
                    <button
                      onClick={() =>
                        navigate(`/assessments/view/${assessment.id}`)
                      }
                      className="px-4 py-2 text-purple-600 border border-purple-600 rounded-lg hover:bg-purple-50 transition-colors font-medium flex items-center"
                    >
                      View Results
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </button>
                  ) : (
                    <button
                      onClick={() =>
                        navigate(`/assessments/fill/${assessment.id}`)
                      }
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center"
                    >
                      {assessment.status === 'in_progress'
                        ? 'Continue'
                        : 'Start Assessment'}
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </button>
                  )}
                </div>
              </div>

              {isUrgent && assessment.status !== 'completed' && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900">
                      Urgent: Due Soon
                    </p>
                    <p className="text-sm text-red-700">
                      This assessment must be completed within the next{' '}
                      {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} to
                      maintain your benefits.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredAssessments.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No assessments found
          </h3>
          <p className="text-gray-600">
            {statusFilter === 'all'
              ? 'You have no assigned assessments at this time.'
              : `You have no ${statusFilter.replace('_', ' ')} assessments.`}
          </p>
        </div>
      )}
    </div>
  );
}
